import { Component, input, output, signal, computed, ChangeDetectionStrategy, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LucideAngularModule, Copy, Check, Edit } from "lucide-angular";
import { CopilotTooltip } from "../directives/tooltip";
import { cn } from "../utils/utils";
import { injectChatLabels } from "../core/chat-labels";

@Component({
  selector: "button[agentChatUserMessageToolbarButton]",
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `<ng-content></ng-content>`,
  host: {
    "[class]": "computedClass()",
    "[attr.disabled]": "disabled() ? true : null",
    type: "button",
    "[attr.aria-label]": "title()",
  },
  hostDirectives: [
    {
      directive: CopilotTooltip,
      inputs: ["copilotTooltip: title", "tooltipPosition", "tooltipDelay"],
    },
  ],
})
export class AgentChatUserMessageToolbarButton {
  title = input<string>("");

  disabled = input<boolean>(false);

  inputClass = input<string | undefined>();

  computedClass = computed(() => cn("achat-msg-btn", this.inputClass()));
}

@Component({
  standalone: true,
  selector: "agent-chat-user-message-copy-button",
  imports: [CommonModule, LucideAngularModule, AgentChatUserMessageToolbarButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      agentChatUserMessageToolbarButton
      [title]="title() || labels.userMessageToolbarCopyMessageLabel"
      [disabled]="disabled()"
      [inputClass]="inputClass()"
      (click)="handleCopy()"
    >
      @if (copied()) {
      <lucide-angular [img]="CheckIcon" [size]="18"></lucide-angular>
      } @else {
      <lucide-angular [img]="CopyIcon" [size]="18"></lucide-angular>
      }
    </button>
  `,
})
export class AgentChatUserMessageCopyButton {
  readonly title = input<string | undefined>();

  readonly disabled = input<boolean>(false);

  readonly inputClass = input<string | undefined>();

  readonly content = input<string | undefined>();

  readonly clicked = output<void>();

  readonly CopyIcon = Copy;

  readonly CheckIcon = Check;

  readonly copied = signal(false);

  readonly labels = injectChatLabels();

  handleCopy(): void {
    if (!this.content()) return;
    this.copied.set(true);
    setTimeout(() => this.copied.set(false), 2000);
    navigator.clipboard.writeText(this.content()!).then(
      () => this.clicked.emit(),
      (err) => {
        console.error("Failed to copy message:", err);
        this.copied.set(false);
      },
    );
  }
}

@Component({
  selector: "agent-chat-user-message-edit-button",
  standalone: true,
  imports: [CommonModule, LucideAngularModule, AgentChatUserMessageToolbarButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button
      agentChatUserMessageToolbarButton
      [title]="title() || labels.userMessageToolbarEditMessageLabel"
      [disabled]="disabled()"
      [inputClass]="inputClass()"
      (click)="handleEdit()"
    >
      <lucide-angular [img]="EditIcon" [size]="18"></lucide-angular>
    </button>
  `,
})
export class AgentChatUserMessageEditButton {
  title = input<string | undefined>();

  disabled = input<boolean>(false);

  inputClass = input<string | undefined>();

  clicked = output<void>();

  readonly EditIcon = Edit;

  readonly labels = injectChatLabels();

  handleEdit(): void {
    if (!this.disabled()) {
      this.clicked.emit();
    }
  }
}
